
import Hero from '@/components/Hero';
import FeaturedProducts from '@/components/FeaturedProducts';
import HowItWorks from '@/components/HowItWorks';
import PlantExpertSection from '@/components/PlantExpertSection';
import Testimonials from '@/components/Testimonials';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { useSiteSettings } from '@/hooks/useSiteSettings';
import { Skeleton } from '@/components/ui/skeleton';
import { SEOHead } from '@/components/SEOHead';
import { generateOrganizationSchema, generateWebsiteSchema, generateLocalBusinessSchema } from '@/lib/seo';

const HandPickedSection = () => {
  const { data: settings, isLoading } = useSiteSettings();

  if (isLoading) {
    return (
      <section className="py-20 bg-neutral-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 lg:gap-16 items-center">
            <div className="rounded-lg overflow-hidden">
              <Skeleton className="w-full h-64" />
            </div>
            <div className="text-center md:text-left">
              <Skeleton className="h-8 w-3/4 mb-4" />
              <Skeleton className="h-6 w-full mb-2" />
              <Skeleton className="h-6 w-5/6 mb-8" />
              <Skeleton className="h-12 w-32" />
            </div>
          </div>
        </div>
      </section>
    );
  }

  const mediaUrl = settings?.handpicked_media_url || "/lovable-uploads/d2c15f1f-4535-4e05-94a3-a9e10975b465.png";
  const mediaType = settings?.handpicked_media_type || 'image';
  const title = settings?.handpicked_title || 'Hand-Picked Premium Plants';
  const description = settings?.handpicked_description || 'We carefully select only the healthiest, most vibrant plants to ship from our greenhouse';

  return (
    <section className="py-20 bg-neutral-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 gap-8 md:gap-12 lg:gap-16 items-center">
          <div className="rounded-lg overflow-hidden">
            {mediaType === 'video' ? (
              <video
                autoPlay
                muted
                loop
                playsInline
                className="object-cover w-full h-full"
              >
                <source src={mediaUrl} type="video/mp4" />
                {/* Fallback to default image if video fails */}
                <img 
                  src="/lovable-uploads/d2c15f1f-4535-4e05-94a3-a9e10975b465.png" 
                  alt={title}
                  className="object-cover w-full h-full"
                />
              </video>
            ) : (
              <img 
                loading="lazy"
                src={mediaUrl} 
                alt={title}
                className="object-cover w-full h-full" 
              />
            )}
          </div>
          <div className="text-center md:text-left">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground">
              {title}
            </h2>
            <p className="mt-4 text-lg text-muted-foreground max-w-md mx-auto md:mx-0">
              {description}
            </p>
            <div className="mt-8">
              <Button size="lg" asChild className="bg-foreground text-primary-foreground hover:bg-foreground/90">
                <Link to="/shop">Shop Plants</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Index = () => {
  const schemas = [
    generateOrganizationSchema(),
    generateWebsiteSchema(),
    generateLocalBusinessSchema()
  ];

  return (
    <>
      <SEOHead
        title="Buy Indoor Plants Online in India"
        description="Shop premium indoor plants with self-watering pots at Paudhify. Free delivery across India, 7-day health guarantee, and expert plant care support. Transform your space with greenery!"
        canonical="/"
        keywords={['buy plants online', 'indoor plants India', 'self watering pots', 'money plant', 'snake plant', 'plant delivery India']}
        jsonLd={schemas}
      />
      <Hero />
      <FeaturedProducts />
      <HowItWorks />
      <HandPickedSection />
      <PlantExpertSection />
      <Testimonials />
    </>
  );
};

export default Index;
